/**
 * Saved 3D view (EarthImager view file, manual 3.8.12-13): one per project in localStorage under
 * `ugis.3d-view:<projectId>`, versioned `v: 1`. deserializeView() validates every field and drops what it does
 * not recognise, so a stale or hand-edited entry can never break the viewer.
 */
export const VIEW_VERSION = 1;
const COLOR_MODE_IDS = ['viridis', 'height', 'rgb', 'intensity', 'classification'];
const RENDER_MODES = ['blocks', 'points'];
const SLICE_AXES = ['none', 'x', 'y', 'z'];
const DRAPE_STYLE_IDS = ['hillshade', 'elevation', 'satellite', 'streets', 'topo'];

export const viewKey = (projectId) => `ugis.3d-view:${projectId}`;

const num = (v) => typeof v === 'number' && Number.isFinite(v);
const unit = (v) => num(v) && v >= 0 && v <= 1;
const pair = (v) => Array.isArray(v) && v.length === 2 && v.every(unit) && v[0] <= v[1];
const vec3 = (v) => Array.isArray(v) && v.length === 3 && v.every(num);
const plain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
// JSON-safe object (primitives, small arrays, depth-limited) for the sections the scene merges over its own defaults
function jsonSafe(v, depth = 0) {
  if (v === null || typeof v === 'string' || typeof v === 'boolean' || num(v)) return true;
  if (depth > 4) return false;
  if (Array.isArray(v)) return v.length <= 64 && v.every((x) => jsonSafe(x, depth + 1));
  if (plain(v)) return Object.values(v).every((x) => jsonSafe(x, depth + 1));
  return false;
}

// ticked datasets: ids of the x/y/z point clouds and of the raw scatters (checked against the loaded datasets on restore)
const idList = (v) => (Array.isArray(v) && v.length <= 200 && v.every((x) => typeof x === 'string' && x.length > 0 && x.length <= 100) ? [...new Set(v)] : null);
// picked iso levels index the colour levels: small non-negative integers
const pickedLevels = (v) => (Array.isArray(v) && v.length <= 64 && v.every((x) => Number.isInteger(x) && x >= 0 && x < 64) ? v : null);

const bool = (v) => typeof v === 'boolean';
const VIEW_FIELDS = {
  colorMode: (v) => COLOR_MODE_IDS.includes(v),
  threshold: pair, cropX: pair, cropZ: pair,
  sliceAxis: (v) => SLICE_AXES.includes(v),
  slicePos: unit, sliceThickness: unit, outsideOpacity: unit, fenceOpacity: unit,
  renderMode: (v) => RENDER_MODES.includes(v),
  showFences: (v) => typeof v === 'boolean',
  showVolumePanel: (v) => typeof v === 'boolean',
  // scene elements (Layers panel 'Scene')
  showGrid: bool, showAxes: bool, showTraces: bool, showSections: bool, showVolumes: bool, showBoreholes: bool, showOverlay: bool,
};
export const VIEW_KEYS = Object.keys(VIEW_FIELDS);

const TERRAIN_FIELDS = {
  drape3d: (v) => typeof v === 'boolean',
  drapeStyle: (v) => DRAPE_STYLE_IDS.includes(v),
  ve3d: (v) => num(v) && v >= 0.1 && v <= 50,
  surfaceOpacity3d: (v) => num(v) && v >= 0.1 && v <= 1,
};
export const TERRAIN_KEYS = Object.keys(TERRAIN_FIELDS);

// base layers of the /3d page (Layers panel): Point Cloud (`datasets`), Projects
const LAYER_FIELDS = { datasets: bool, projects: bool };
export const LAYER_KEYS = Object.keys(LAYER_FIELDS);

function pick(src, fields) {
  if (!plain(src)) return undefined;
  const out = {};
  Object.keys(fields).forEach((k) => { if (fields[k](src[k])) out[k] = src[k]; });
  return Object.keys(out).length ? out : undefined;
}

/** Snapshot { view, camera, extras, terrain, layers, datasets: { drawn, scatter } } -> the stored object (unknown or invalid fields dropped). */
export function serializeView(snapshot) {
  const out = { v: VIEW_VERSION };
  const view = pick(snapshot?.view, VIEW_FIELDS);
  if (view) out.view = view;
  const c = snapshot?.camera;
  if (plain(c) && vec3(c.position) && vec3(c.target)) {
    out.camera = { position: [...c.position], target: [...c.target] };
    // the geographic anchor of the scene's local frame; absent in entries saved before it was recorded
    if (plain(c.origin) && num(c.origin.lon) && num(c.origin.lat)) out.camera.origin = { lon: c.origin.lon, lat: c.origin.lat };
  }
  const extras = {};
  ['colour', 'iso', 'slices', 'cut'].forEach((k) => {
    const e = snapshot?.extras?.[k];
    if (!plain(e) || !jsonSafe(e)) return;
    extras[k] = k === 'iso' && e.picked && !pickedLevels(e.picked) ? { ...e, picked: null } : e;
  });
  if (Object.keys(extras).length) out.extras = extras;
  const terrain = pick(snapshot?.terrain, TERRAIN_FIELDS);
  if (terrain) out.terrain = terrain;
  const layers = pick(snapshot?.layers, LAYER_FIELDS);
  if (layers) out.layers = layers;
  const drawn = idList(snapshot?.datasets?.drawn), scatter = idList(snapshot?.datasets?.scatter);
  if (drawn?.length || scatter?.length) out.datasets = { drawn: drawn || [], scatter: scatter || [] };
  return out;
}

/** Stored text (or object) -> a validated view, or null when it is missing, malformed or another version. */
export function deserializeView(raw) {
  let data = raw;
  if (typeof raw === 'string') {
    try { data = JSON.parse(raw); } catch { return null; }
  }
  if (!plain(data) || data.v !== VIEW_VERSION) return null;
  return serializeView(data);
}

const defaultStorage = () => { try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; } };

export function loadView(projectId, storage = defaultStorage()) {
  if (!projectId || !storage) return null;
  try { return deserializeView(storage.getItem(viewKey(projectId))); } catch { return null; }
}

export function saveView(projectId, snapshot, storage = defaultStorage()) {
  if (!projectId || !storage) return false;
  try { storage.setItem(viewKey(projectId), JSON.stringify(serializeView(snapshot))); return true; } catch { return false; }
}

export function clearView(projectId, storage = defaultStorage()) {
  if (!projectId || !storage) return;
  try { storage.removeItem(viewKey(projectId)); } catch { /* storage unavailable */ }
}
